/**
 * Wie oft zeichnet das Gerät gerade? — aus den Wischen, die lib/scroll-takt.ts ohnehin misst.
 * (02.10.2026, pre.3600)
 *
 * Martin, 02.10. ~14:25: *„Das Scrollen macht wieder Probleme oder immer noch Probleme."*
 *
 * ## Was das Journal an dem Tag sagte (nur Zahlen, `[scroll-t] art=takt`)
 *
 * - iPhone (Chat-Sicht 547 Punkte, Fenster 852): bis 09:54 lag die mittlere Bildzeit je Wisch bei
 *   17 ms (60 Bilder/s). Ab 10:12 bis 14:27 waren es in 69 von 70 Wischen 33 ms — die halbe
 *   Bildrate, durchgehend, in Chat, Einstellungen und Zuhause. Lücken über 50 ms blieben selten
 *   (14 Uhr: 7 von 847 Bildern): Das Skript stockte nicht, das Gerät zeichnete seltener.
 *   Am Vormittag war das als Stromsparmodus erkannt worden (Ticket 2912).
 * - Mac (Chrome, Sicht 653): zur selben Zeit 16,7 ms.
 * - Sprünge durch die App gab es in dem Zeitraum keine: Alle 48 Zeilen mit Auslöser (Zuklappen,
 *   Werkzeugkarte, Neuaufbau am Zugende) fielen auf einen Leser, der am Ende stand, und der
 *   Verlauf folgte um genau die Höhenänderung (oder hielt den Ort, `gehalten=ja`).
 *
 * Die App kann eine halbierte Bildrate nicht ausgleichen. Sie kann sie aber ehrlich SAGEN, statt
 * dass Martin einen Fehler sucht, den es nicht gibt. Diese Datei entscheidet, wann.
 *
 * ## Die Regel
 *
 * Je Wisch (mindestens 12 Bilder und 250 ms, sonst sagt er nichts):
 *   - voll    Bildzeit (Median) unter 22 ms — 60 Bilder/s oder mehr.
 *   - halb    Median 30–40 ms und höchstens jedes zehnte Bild über 50 ms — gleichmäßig, nur selten.
 *   - stockt  alles andere (Median über 40, viele lange Lücken): Dann hängt das Skript, nicht das
 *             Gerät — kein Hinweis auf den Stromsparmodus, das wäre eine falsche Ausrede.
 * „Halbe Bildrate“ gilt erst nach mindestens zwei halben Wischen hintereinander mit zusammen
 * mindestens 3 s Bewegung. Ein einziger voller Wisch nimmt es sofort zurück.
 *
 * ⚠️ Gemessen wird mit `requestAnimationFrame`. Ob das System das Scrollen selbst ebenfalls mit
 *    30 Bildern zeichnet, sieht die Seite nicht — sicher ist: alles, was die Seite zeichnet,
 *    kommt nur halb so oft.
 */
import type { Takt } from "./scroll-takt.js";
import { geraeteSuffix } from "./geraete-zustand.js";
import { meldeSpur } from "./mikro-spur.js";

/** Unter dieser Bildzeit (ms, Median) zeichnet das Gerät voll. */
const VOLL_BIS_MS = 22;
/** In diesem Bereich (ms, Median) zeichnet es mit halber Rate (30 Bilder/s ≈ 33 ms). */
const HALB_AB_MS = 30;
const HALB_BIS_MS = 40;
/** Höchstens dieser Anteil der Bilder darf über 50 ms liegen, sonst stockt das Skript. */
const STOCKT_ANTEIL = 0.1;
/** So viel Bewegung (ms) mit halber Rate, bevor es als Stand gilt … */
export const HALB_DAUER_MS = 3000;
/** … verteilt auf mindestens so viele Wische. */
const HALB_MIN_WISCHE = 2;
/** Ein Wisch sagt erst ab so vielen Bildern und Millisekunden etwas (wie lib/scroll-takt.ts). */
const MIN_BILDER = 12;
const MIN_MS = 250;

export type BildrateStand = "unbekannt" | "voll" | "halb";
export type WischArt = "kurz" | "voll" | "halb" | "stockt";

/** Reine Rechnung: Was sagt dieser eine Wisch über die Bildrate? */
export function ordneWisch(t: Pick<Takt, "bilder" | "dauerMs" | "medianMs" | "luecken50">): WischArt {
  if (t.bilder < MIN_BILDER || t.dauerMs < MIN_MS) return "kurz";
  if (t.medianMs < VOLL_BIS_MS) return "voll";
  const gleichmaessig = t.luecken50 <= t.bilder * STOCKT_ANTEIL;
  if (t.medianMs >= HALB_AB_MS && t.medianMs <= HALB_BIS_MS && gleichmaessig) return "halb";
  return "stockt";
}

export interface BildrateZaehler {
  stand: BildrateStand;
  /** Bewegung (ms) und Zahl der halben Wische seit dem letzten vollen oder stockenden. */
  halbMs: number;
  halbWische: number;
}

export function neuerZaehler(): BildrateZaehler {
  return { stand: "unbekannt", halbMs: 0, halbWische: 0 };
}

/** Reine Rechnung: einen Wisch verbuchen. */
export function bucheWisch(z: BildrateZaehler, art: WischArt, dauerMs: number): BildrateZaehler {
  switch (art) {
    case "kurz":
      return z;
    case "voll":
      return { stand: "voll", halbMs: 0, halbWische: 0 };
    case "stockt":
      // Kein Urteil über das Gerät — aber die Reihe der halben Wische ist unterbrochen.
      return { stand: z.stand, halbMs: 0, halbWische: 0 };
    case "halb": {
      const halbMs = z.halbMs + dauerMs;
      const halbWische = z.halbWische + 1;
      const halb = halbMs >= HALB_DAUER_MS && halbWische >= HALB_MIN_WISCHE;
      return { stand: halb ? "halb" : z.stand, halbMs, halbWische };
    }
  }
}

type Hoerer = (stand: BildrateStand) => void;
let zaehler = neuerZaehler();
const hoerer = new Set<Hoerer>();

/** Der jetzige Stand auf diesem Gerät (seit dem Laden der Seite). */
export function bildrateStand(): BildrateStand {
  return zaehler.stand;
}

/** Bei jedem Wechsel des Stands Bescheid geben. Gibt das Abmelden zurück. */
export function beiBildrate(fn: Hoerer): () => void {
  hoerer.add(fn);
  return () => {
    hoerer.delete(fn);
  };
}

/** Aus lib/scroll-takt.ts: ein Wisch ist zu Ende. Ein Wechsel von oder nach „halb“ geht als eine Zeile ins Journal. */
export function bucheBildrate(t: Takt): void {
  const vorher = zaehler.stand;
  zaehler = bucheWisch(zaehler, ordneWisch(t), t.dauerMs);
  if (zaehler.stand === vorher) return;
  // Ins Journal nur, was „halb“ betrifft (an/aus) — „unbekannt → voll“ nach jedem Laden wäre Rauschen.
  if (zaehler.stand === "halb" || vorher === "halb") meldeSpur("scroll-t", `art=bildrate stand=${zaehler.stand} vorher=${vorher} median=${t.medianMs} bilder=${t.bilder}${geraeteSuffix()}`);
  for (const fn of [...hoerer]) {
    try {
      fn(zaehler.stand);
    } catch {
      /* Ein Hörer darf die anderen nicht aufhalten. */
    }
  }
}
